/**
 * ACT-1295: AI Summary settings + cache control.
 *
 * Two admin controls for the connect-sourced clinical summary:
 *  1. Section gate — four toggles (claims / cases / labs / authorizations) that
 *     decide which data feeds the summary. Persists on every toggle to
 *     member_360.patient_summary_settings via updatePatientSummarySettings.
 *  2. Clear cache — purges every cached summary in Redis (all patients). The
 *     next request per patient re-sources and re-caches.
 *
 * GraphQL operations (backend resolvers live in care-management-gql):
 *   Query:    AcPatientSummarySettings       → patientSummarySettings
 *   Mutation: AcUpdatePatientSummarySettings  → updatePatientSummarySettings
 *   Mutation: AcClearPatientSummaryCache       → clearPatientSummaryCache (Int)
 */
import { useMutation, useQuery } from "@apollo/client";
import { RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

import LoadingSpinner from "../../components/LoadingSpinner";
import { AdminWall } from "../components/AdminWall";
import { Toggle } from "../components/Toggle";
import {
  CLEAR_PATIENT_SUMMARY_CACHE_MUTATION,
  PATIENT_SUMMARY_SETTINGS_QUERY,
  UPDATE_PATIENT_SUMMARY_SETTINGS_MUTATION,
  type ClearPatientSummaryCacheResult,
  type PatientSummarySettingsResult,
  type UpdatePatientSummarySettingsResult,
  type UpdatePatientSummarySettingsVars,
} from "../graphql/adminOps";

// Toggle moved to ../components/Toggle when VisitNoteTypeCard became the third
// consumer — same markup, now shared rather than copied a third time.

// The four editable section flags, in display order. `key` matches the
// PatientSummarySettings boolean field names 1:1.
const SECTIONS: {
  key: "claimsEnabled" | "casesEnabled" | "labsEnabled" | "authsEnabled";
  label: string;
  description: string;
}[] = [
  {
    key: "claimsEnabled",
    label: "Claims",
    description: "Include claims history (procedures, place of service) in the summary.",
  },
  {
    key: "casesEnabled",
    label: "Cases",
    description: "Include care-management cases in the summary.",
  },
  {
    key: "labsEnabled",
    label: "Labs",
    description: "Include lab results in the summary.",
  },
  {
    key: "authsEnabled",
    label: "Authorizations",
    description: "Include prior authorizations in the summary.",
  },
];

export default function AiSummaryPage() {
  const { data, previousData, loading, error, refetch } =
    useQuery<PatientSummarySettingsResult>(PATIENT_SUMMARY_SETTINGS_QUERY, {
      fetchPolicy: "cache-first",
    });

  const [updateSettings, { loading: saving }] = useMutation<
    UpdatePatientSummarySettingsResult,
    UpdatePatientSummarySettingsVars
  >(UPDATE_PATIENT_SUMMARY_SETTINGS_MUTATION, {
    // Write the persisted row straight into the cache so the toggle sticks.
    update(cache, { data: mutData }) {
      if (!mutData) return;
      cache.writeQuery<PatientSummarySettingsResult>({
        query: PATIENT_SUMMARY_SETTINGS_QUERY,
        data: { patientSummarySettings: mutData.updatePatientSummarySettings },
      });
    },
  });

  const [clearCache, { loading: clearing }] =
    useMutation<ClearPatientSummaryCacheResult>(
      CLEAR_PATIENT_SUMMARY_CACHE_MUTATION,
    );

  const settings = (data ?? previousData)?.patientSummarySettings;

  const handleToggle = (
    key: (typeof SECTIONS)[number]["key"],
    value: boolean,
  ) => {
    if (!settings) return;
    // Every update sends the full set of four flags (the mutation replaces the
    // row), so start from the current settings and override the one that moved.
    const next: UpdatePatientSummarySettingsVars["input"] = {
      claimsEnabled: settings.claimsEnabled,
      casesEnabled: settings.casesEnabled,
      labsEnabled: settings.labsEnabled,
      authsEnabled: settings.authsEnabled,
      [key]: value,
    };
    void updateSettings({ variables: { input: next } }).catch((e) => {
      toast.error(
        e instanceof Error ? e.message : "Failed to update settings",
      );
    });
  };

  const handleClearCache = () => {
    if (
      !window.confirm(
        "Clear all cached patient summaries? The next request per patient will regenerate from source.",
      )
    ) {
      return;
    }
    void clearCache()
      .then((res) => {
        const count = res.data?.clearPatientSummaryCache ?? 0;
        toast.success(
          count === 1
            ? "Cleared 1 cached summary."
            : `Cleared ${count} cached summaries.`,
        );
      })
      .catch((e) => {
        toast.error(e instanceof Error ? e.message : "Failed to clear cache");
      });
  };

  const updatedLabel =
    settings?.updatedAt &&
    new Date(settings.updatedAt).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div>
      <AdminWall error={error} screenLabel="AI Summary">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-bold">AI Summary</h1>
          <button
            type="button"
            onClick={() => void refetch()}
            className="p-2 rounded-lg border border-border hover:bg-accent transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Control which data feeds the clinical patient summary and clear the
          cached results. Changes persist immediately.
        </p>

        {loading && !settings ? (
          <LoadingSpinner fullScreen={false} label="Loading settings..." />
        ) : error ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3">
            <p className="text-sm text-destructive font-medium">
              Failed to load AI summary settings
            </p>
            <p className="text-xs text-destructive/80 mt-1">{error.message}</p>
          </div>
        ) : settings ? (
          <div className="flex flex-col gap-6">
            {/* Section gate */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-sm font-semibold m-0">Summary sections</h2>
                {updatedLabel && (
                  <span className="text-[10px] text-muted-foreground/60">
                    · updated {updatedLabel}
                    {settings.updatedBy ? ` by ${settings.updatedBy}` : ""}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-3">
                {SECTIONS.map((section) => (
                  <div
                    key={section.key}
                    className="flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3.5"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm m-0">
                        {section.label}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 m-0">
                        {section.description}
                      </p>
                    </div>
                    <Toggle
                      enabled={settings[section.key]}
                      onChange={(v) => handleToggle(section.key, v)}
                      disabled={saving}
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Cache control */}
            <section>
              <h2 className="text-sm font-semibold m-0 mb-2">Cache</h2>
              <div className="flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3.5">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm m-0">
                    Clear summary cache
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 m-0">
                    Purge every cached patient summary. Use after changing the
                    prompt or sections so summaries regenerate from source.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClearCache}
                  disabled={clearing}
                  className="inline-flex items-center gap-2 rounded-lg border border-destructive/40 text-destructive px-3 py-2 text-sm font-medium hover:bg-destructive/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" aria-hidden="true" />
                  {clearing ? "Clearing..." : "Clear cache"}
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </AdminWall>
    </div>
  );
}
